import { useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { Input } from '@/components/ui/input';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { ImageIcon, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MediaItem {
  url: string;
  type: 'image' | 'video' | 'audio';
  eventId?: string;
  author?: string;
}

const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.avif', '.svg'];
const VIDEO_EXTS = ['.mp4', '.webm', '.mov', '.mkv'];
const AUDIO_EXTS = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];

function getMediaType(url: string): 'image' | 'video' | 'audio' | null {
  const lower = url.toLowerCase();
  if (IMAGE_EXTS.some((ext) => lower.includes(ext))) return 'image';
  if (VIDEO_EXTS.some((ext) => lower.includes(ext))) return 'video';
  if (AUDIO_EXTS.some((ext) => lower.includes(ext))) return 'audio';
  return null;
}

/** Resolve a hex pubkey, npub, or nprofile to hex (undefined if invalid). */
function resolveAuthorInput(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed.toLowerCase();
  try {
    const decoded = nip19.decode(trimmed);
    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return decoded.data.pubkey;
  } catch { /* not a NIP-19 identifier */ }
  return undefined;
}

function MediaCard({ item }: { item: MediaItem }) {
  const [isLoaded, setIsLoaded] = useState(false);

  if (item.type === 'image') {
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative aspect-square rounded-lg overflow-hidden bg-muted border border-border/50"
      >
        <img
          src={item.url}
          alt=""
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <ExternalLink className="w-5 h-5 text-white drop-shadow-lg" />
        </div>
      </a>
    );
  }

  if (item.type === 'video') {
    return (
      <div className="relative aspect-square rounded-lg overflow-hidden bg-muted border border-border/50">
        <video
          src={item.url}
          className="w-full h-full object-cover"
          controls
          preload="metadata"
        />
      </div>
    );
  }

  return (
    <div className="relative aspect-square rounded-lg overflow-hidden bg-muted border border-border/50 p-2 flex items-center justify-center">
      <audio src={item.url} controls className="w-full" />
    </div>
  );
}

export function MediaApp() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [searchPubkey, setSearchPubkey] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const targetPubkey = resolveAuthorInput(searchPubkey) ?? (searchPubkey.trim() ? undefined : user?.pubkey);

  const { data: mediaItems, isLoading } = useQuery({
    queryKey: ['media', targetPubkey],
    queryFn: async ({ signal }) => {
      if (!targetPubkey) return [];

      const events = await nostr.query(
        [
          { kinds: [1063], authors: [targetPubkey], limit: 50 },
          { kinds: [1], authors: [targetPubkey], limit: 100 },
        ],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) }
      );

      const items: MediaItem[] = [];
      const seenUrls = new Set<string>();

      const pushUrl = (rawUrl: string, event: { id: string; pubkey: string }) => {
        // Sanitize event-sourced URLs before they reach href/src attributes
        const url = sanitizeUrl(rawUrl);
        if (!url || seenUrls.has(url)) return;
        const type = getMediaType(url);
        if (!type) return;
        items.push({ url, type, eventId: event.id, author: event.pubkey });
        seenUrls.add(url);
      };

      events.forEach((event) => {
        if (event.kind === 1063) {
          const url = event.tags.find(([n]) => n === 'url')?.[1];
          if (url) pushUrl(url, event);
        } else if (event.kind === 1) {
          // Extract URLs from content
          const urlRegex = /(https?:\/\/[^\s]+)/g;
          const matches = event.content.match(urlRegex) || [];
          matches.forEach((url) => pushUrl(url, event));
        }
      });

      return items;
    },
    enabled: !!targetPubkey,
  });

  const filteredItems = mediaItems?.filter((item) => {
    if (!searchQuery) return true;
    return item.url.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border/50 bg-muted/30 space-y-2">
        <h2 className="text-sm font-semibold">Media Browser</h2>
        <div className="flex gap-2">
          <Input
            value={searchPubkey}
            onChange={(e) => setSearchPubkey(e.target.value)}
            placeholder="npub or hex pubkey..."
            className="h-8 text-xs"
          />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter media..."
            className="h-8 text-xs"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-lg bg-muted animate-pulse border border-border/50" />
            ))}
          </div>
        ) : filteredItems && filteredItems.length > 0 ? (
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {filteredItems.map((item, i) => (
              <MediaCard key={`${item.url}-${i}`} item={item} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground px-6 text-center">
            <ImageIcon className="w-10 h-10 mb-2 opacity-40" />
            <p className="text-sm">
              {searchPubkey.trim() && !targetPubkey
                ? 'That doesn\'t look like a valid pubkey, npub, or nprofile.'
                : 'No media found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
